import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import LookupPicker from '../additional_payments/lookup_picker';
import { discard_guard_text } from '../shared/dialog_logic';
import { DialogFooter, ErrorBox, FieldError, NoteField } from './dialog_parts';
import { month_label } from '../format';
import { KIND, suggested_month } from './error_handlers';
import { row_view } from './adapters';
import { MODE, add_form_errors } from './form_errors';
import {
  MANUAL_TYPE,
  PENALTY_TYPES,
  is_pass_eligible,
  prefill_pct,
  reason_options,
  unchanged_since
} from './params';

/*
 * "Add penalty": session-linked (any type, AV_LATE included, which the engine never derives) or
 * month-level (type MANUAL, amount only: overpayment recovery). The month is the tab's month.
 */
const blank = (client_request_id) => ({
  client_request_id,
  teacher_uuid: '',
  teacher_name: '',
  session_uuid: '',
  session_label: '',
  penalty_type: '',
  penalty_pct: '',
  amount: '',
  reason_code: '',
  note: ''
});

const session_text = (s) =>
  [
    s.start_time_ist,
    s.type,
    s.duration_mins ? `${s.duration_mins} mins` : '',
    s.status,
    s.student_name
  ]
    .filter(Boolean)
    .join(' · ');

const AddModal = ({
  month_text,
  saving,
  error,
  new_request_id,
  search_teachers,
  search_sessions,
  on_submit,
  on_close,
  on_focus_lost,
  on_move,
  default_pcts,
  seed
}) => {
  const [form, setForm] = useState(() => ({ ...blank(new_request_id()), ...seed }));
  const [mode, setMode] = useState(MODE.PCT);
  const [confirms, setConfirms] = useState({});
  const [touched, setTouched] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const month_level = form.penalty_type === MANUAL_TYPE;
  const by_amount = month_level || mode === MODE.AMOUNT;
  const errors = add_form_errors(form, mode);
  const server = (error && error.fields) || {};
  const body = {
    ...form,
    penalty_pct: by_amount ? '' : form.penalty_pct,
    amount: by_amount ? form.amount : ''
  };
  const [sent, setSent] = useState(null);

  // Earlier confirms answered the body the server refused; an edited body starts without them.
  const send = (extra) => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    const next = { ...(unchanged_since(sent, body) ? confirms : {}), ...extra };
    setConfirms(next);
    setSent(body);
    on_submit(body, next);
  };

  const confirmable = !!error && error.kind === KIND.CONFIRM && unchanged_since(sent, body);
  // A confirm the edit dropped takes its sentence and duplicate list with it.
  const shown = error && error.kind === KIND.CONFIRM && !confirmable ? null : error;
  const confirm = () => send(error.confirm ? { [error.confirm]: true } : {});
  const move_month = on_move ? suggested_month(error) : '';
  // Another month is another request: a new request id, and no confirm carried over.
  const move = () => {
    setForm((f) => ({ ...f, client_request_id: new_request_id() }));
    setConfirms({});
    setSent(null);
    on_move(move_month);
  };
  const existing =
    error && error.kind === KIND.ALREADY_SAVED && error.existing ? row_view(error.existing) : null;

  return (
    <TpDialog
      title_id="tp-pe-add-title"
      busy={saving}
      guard_text={discard_guard_text(form.note)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal">
        <h4 id="tp-pe-add-title">Add penalty · {month_text}</h4>
        <LookupPicker
          id="tp-pe-add-teacher"
          name="teacher"
          label="Teacher"
          placeholder="Name or email"
          min_chars={2}
          value_text={form.teacher_name}
          search={search_teachers}
          option_key={(t) => t.teacher_uuid}
          render_option={(t) => (
            <span>
              <b>{t.name}</b> · {t.email}
            </span>
          )}
          on_pick={(t) =>
            setForm((f) => ({
              ...f,
              teacher_uuid: t.teacher_uuid,
              teacher_name: t.name,
              session_uuid: '',
              session_label: ''
            }))
          }
          on_clear={() =>
            setForm((f) => ({
              ...f,
              teacher_uuid: '',
              teacher_name: '',
              session_uuid: '',
              session_label: ''
            }))
          }
          error_text={(touched && errors.teacher_uuid) || server.teacher_uuid || ''}
        />
        <div className="tp-field">
          <label htmlFor="tp-pe-add-type">Penalty type</label>
          <select
            id="tp-pe-add-type"
            className="tp-select"
            value={form.penalty_type}
            onChange={(e) => {
              const next = e.target.value;
              setForm((f) => ({
                ...f,
                penalty_type: next,
                penalty_pct: prefill_pct(f.penalty_pct, f.penalty_type, next, default_pcts)
              }));
            }}
          >
            <option value="">Choose a type</option>
            {PENALTY_TYPES.map((t) => (
              <option key={t.penalty_type} value={t.penalty_type}>
                {t.label}
              </option>
            ))}
          </select>
          <FieldError text={(touched && errors.penalty_type) || server.penalty_type} />
          {month_level ? (
            <span className="tp-hint">
              A month-level penalty has no session, for example an overpayment recovery. It is
              charged in {month_text}.
            </span>
          ) : null}
          {is_pass_eligible(form.penalty_type) ? (
            <span className="tp-hint">
              If the session's day holds a Free Pass, the pass covers this penalty too.
            </span>
          ) : null}
        </div>
        {month_level ? null : (
          <LookupPicker
            id="tp-pe-add-session"
            name="session"
            label="Session"
            placeholder="Search this month's sessions"
            min_chars={0}
            value_text={form.session_label}
            disabled={!form.teacher_uuid}
            disabled_hint="Choose a teacher first."
            search={(q) => search_sessions(form.teacher_uuid, q)}
            option_key={(s) => s.session_uuid}
            render_option={(s) => <span>{session_text(s)}</span>}
            on_pick={(s) =>
              setForm((f) => ({
                ...f,
                session_uuid: s.session_uuid,
                session_label: session_text(s)
              }))
            }
            on_clear={() => setForm((f) => ({ ...f, session_uuid: '', session_label: '' }))}
            error_text={(touched && errors.session_uuid) || server.session_uuid || ''}
          />
        )}
        <div className="tp-field-2">
          {month_level ? null : (
            <div className="tp-field">
              <span className="tp-card-field-label">Charge by</span>
              <div className="tp-seg" role="group" aria-label="Charge by">
                <button
                  type="button"
                  className={mode === MODE.PCT ? 'on' : ''}
                  aria-pressed={mode === MODE.PCT}
                  onClick={() => setMode(MODE.PCT)}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  className={mode === MODE.AMOUNT ? 'on' : ''}
                  aria-pressed={mode === MODE.AMOUNT}
                  onClick={() => setMode(MODE.AMOUNT)}
                >
                  Amount
                </button>
              </div>
            </div>
          )}
          {by_amount ? (
            <div className="tp-field">
              <label htmlFor="tp-pe-add-value">Penalty amount (₹)</label>
              <input
                id="tp-pe-add-value"
                className="tp-input"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
              />
              <FieldError text={(touched && errors.value) || server.amount} />
            </div>
          ) : (
            <div className="tp-field">
              <label htmlFor="tp-pe-add-value">Penalty % of the session's adjusted fee</label>
              <input
                id="tp-pe-add-value"
                className="tp-input"
                inputMode="decimal"
                value={form.penalty_pct}
                onChange={(e) => set('penalty_pct', e.target.value)}
              />
              <FieldError text={(touched && errors.value) || server.penalty_pct} />
            </div>
          )}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-pe-add-reason">Reason</label>
          <select
            id="tp-pe-add-reason"
            className="tp-select"
            value={form.reason_code}
            onChange={(e) => set('reason_code', e.target.value)}
          >
            <option value="">Choose a reason</option>
            {reason_options('add').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <FieldError text={(touched && errors.reason_code) || server.reason_code} />
        </div>
        <NoteField
          id="tp-pe-add-note"
          label="Note (internal, required)"
          value={form.note}
          on_change={(v) => set('note', v)}
          error_text={(touched && errors.note) || server.note}
          placeholder="What happened and how it was checked"
        />
        <ErrorBox error={shown} />
        {move_month ? (
          <button type="button" className="tp-link" onClick={move}>
            {`Add to ${month_label(move_month)} instead`}
          </button>
        ) : null}
        {shown && shown.matches.length ? (
          <ul className="tp-dup-list">
            {shown.matches.map(row_view).map((m) => (
              <li key={m.uuid}>
                {m.short_id} · {m.type_text} · {m.charged_text} · {m.waiver_text}
              </li>
            ))}
          </ul>
        ) : null}
        {existing ? (
          <div className="tp-ctx">
            Saved: {existing.short_id} · {existing.teacher_name} · {existing.type_text} ·{' '}
            {existing.original_text}
          </div>
        ) : null}
        <DialogFooter
          saving={saving}
          error={shown}
          submit_label="Add penalty"
          confirm_label="Add it anyway"
          on_submit={() => send({})}
          on_confirm={confirm}
          on_close={on_close}
        />
      </div>
    </TpDialog>
  );
};

export default AddModal;
